import { Calendar, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { useState } from "react";

function DatePicker({ value, onChange, placeholder, onOpenChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(
    value ? new Date(value) : new Date(),
  );
  const [selectedDate, setSelectedDate] = useState(
    value ? new Date(value) : null,
  );
  const [selectedTime, setSelectedTime] = useState(
    value ? new Date(value).toTimeString().slice(0, 5) : "12:00",
  );

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const generateCalendarDays = (date) => {
    const days = [];
    const totalDays = daysInMonth(date);
    const firstDay = getFirstDayOfMonth(date);

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= totalDays; i++) {
      days.push(i);
    }

    return days;
  };

  const handleDateSelect = (day) => {
    if (day) {
      const newDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day,
      );
      setSelectedDate(newDate);
    }
  };

  const handleTimeChange = (e) => {
    setSelectedTime(e.target.value);
  };

  const handleConfirm = () => {
    if (selectedDate) {
      const [hours, minutes] = selectedTime.split(":");
      const finalDate = new Date(selectedDate);

      finalDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

      onChange(finalDate.toISOString());
      setIsOpen(false);

      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    if (onOpenChange) {
      onOpenChange(false);
    }
    if (value) {
      setSelectedDate(new Date(value));
      setSelectedTime(new Date(value).toTimeString().slice(0, 5));
    }
  };

  const prevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day) => {
    return (
      selectedDate &&
      day === selectedDate.getDate() &&
      currentDate.getMonth() === selectedDate.getMonth() &&
      currentDate.getFullYear() === selectedDate.getFullYear()
    );
  };

  const formatDisplayValue = () => {
    if (value) {
      const date = new Date(value);
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    }
    return placeholder || "Select date and time";
  };

  const handleToggle = () => {
    const newOpenState = !isOpen;
    setIsOpen(newOpenState);
    if (onOpenChange) {
      onOpenChange(newOpenState);
    }
  };

  return (
    <div className="custom-date-picker">
      <div className="date-input-wrapper" onClick={handleToggle}>
        <Calendar className="create-input-icon" size={20} />
        <input
          type="text"
          value={formatDisplayValue()}
          readOnly
          placeholder={placeholder || "Select date and time"}
          className="date-display-input"
        />
      </div>

      {isOpen && (
        <div className="date-picker-popup">
          <div className="date-picker-header">
            <h3>Select Date & Time</h3>
            <div className="date-picker-controls">
              <button onClick={prevMonth} className="month-nav-btn">
                <ChevronLeft size={16} />
              </button>
              <span className="current-month">
                {months[currentDate.getMonth()]} {currentDate.getFullYear()}
              </span>
              <button onClick={nextMonth} className="month-nav-btn">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          <div className="calendar-grid">
            <div className="calendar-weekdays">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day} className="weekday">
                  {day}
                </div>
              ))}
            </div>

            <div className="calendar-days">
              {generateCalendarDays(currentDate).map((day, index) => (
                <div
                  key={index}
                  className={`calendar-day ${!day ? "empty" : ""} ${
                    isToday(day) ? "today" : ""
                  } ${isSelected(day) ? "selected" : ""}`}
                  onClick={() => handleDateSelect(day)}
                >
                  {day}
                </div>
              ))}
            </div>
          </div>

          <div className="time-selection">
            <div className="time-input-wrapper">
              <Clock className="time-icon" size={16} />
              <input
                type="time"
                value={selectedTime}
                onChange={handleTimeChange}
                className="time-input"
              />
            </div>
          </div>

          <div className="date-picker-actions">
            <button onClick={handleCancel} className="cancel-btn">
              Cancel
            </button>
            <button onClick={handleConfirm} className="confirm-btn">
              Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatePicker;
